import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  ArrowDownToLine,
  Check,
  ChevronRight,
  Download,
  X,
} from "lucide-react";
import { readSpreadsheetFile } from "../../../components/YFSImportWizard/parseSpreadsheet";
import { PRESET_KLIMAKOMPASSET, mergePresetIntoMapping } from "../../../components/YFSImportWizard/importPresets";
import { applyYfsImport, mapRowByColumnMapping } from "../../../components/YFSImportWizard/applyYfsImport";
import { resolveFlow, AUTOMATED_FLOW_KEYS } from "../../../components/YFSImportWizard/flowRouting";

const NAVY = "#042C53";
const AMBER = "#FFCC00";
const GREEN = "#085041";
const GREEN_BG = "rgba(15,110,86,0.08)";
const SURFACE = "#F4F5F7";
const BORDER = "rgba(4,44,83,0.10)";

/**
 * This is a UI-port of Lovable's `ImportDataModal.tsx` Klimakompasset flow.
 * It keeps our existing import logic (`applyYfsImport`) and preset mapping,
 * but renders the exact 3-step Lovable UI.
 */

/** @typedef {1|2|3} Step */

const monthLabel = (m) =>
  ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"][m - 1] ?? "";

const prettyPeriod = (start, end) => {
  const s = String(start || "").split("-");
  const e = String(end || "").split("-");
  if (s.length < 3 || e.length < 3) return `${start} – ${end}`;
  return `${s[2]}. ${monthLabel(+s[1])} ${s[0]} – ${e[2]}. ${monthLabel(+e[1])} ${e[0]}`;
};

const formatDate = (v) => {
  if (!v) return "";
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === "number") {
    const d = XLSX.SSF.parse_date_code(v);
    if (d) return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
  }
  return String(v);
};

const parseKlimakompassetMeta = async (file) => {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array", cellDates: true });
  const stam = wb.Sheets["Stamdata"] ?? wb.Sheets[wb.SheetNames[0]];
  const stamRows = XLSX.utils.sheet_to_json(stam, { header: 1, defval: "" });
  const cell = (r, c) => {
    const row = stamRows?.[r];
    return row ? String(row[c] ?? "") : "";
  };
  const companyName = cell(7, 1);
  const yearStartRaw = stamRows?.[13]?.[1] ?? "";
  const yearEndRaw = stamRows?.[16]?.[1] ?? "";
  const yearStart = formatDate(yearStartRaw);
  const yearEnd = formatDate(yearEndRaw);
  const cvr = String(stamRows?.[29]?.[1] ?? "").replace(/\D/g, "");
  const yearLabelMatch = String(yearEnd || yearStart).match(/(\d{4})/);
  const yearLabel = yearLabelMatch ? yearLabelMatch[1] : "—";
  return { companyName, cvr, yearStart, yearEnd, yearLabel };
};

export default function KlimakompassetImportLovable({
  open,
  onClose,
  businessId,
  facilityVat,
  facilityAccountingPeriod,
  accountingPeriodOptions = [],
}) {
  const [step, setStep] = useState(/** @type {Step} */ (1));
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showData, setShowData] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(facilityAccountingPeriod ?? "");
  const [parseError, setParseError] = useState("");

  const [parsed, setParsed] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setLoading(false);
      setProgress(0);
      setShowData(false);
      setSelectedPeriod(facilityAccountingPeriod ?? "");
      setParseError("");
      setParsed(null);
      setImportResult(null);
    }
  }, [open, facilityAccountingPeriod]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleFile = async (file) => {
    setParseError("");
    try {
      const meta = await parseKlimakompassetMeta(file).catch(() => ({
        companyName: "",
        cvr: "",
        yearStart: "",
        yearEnd: "",
        yearLabel: "—",
      }));
      const sheetParsed = await readSpreadsheetFile(file);
      const mapping = mergePresetIntoMapping(PRESET_KLIMAKOMPASSET, sheetParsed.headers);
      const rows = (sheetParsed.rows || []).map((raw) => {
        const mapped = mapRowByColumnMapping(raw, mapping);
        const flow = resolveFlow(mapped);
        const qty = mapped.quantity;
        const qtyMissing =
          qty == null || qty === "" || (typeof qty === "number" && qty === 0);
        const status = qtyMissing
          ? "missing-price"
          : AUTOMATED_FLOW_KEYS.has(flow.key)
          ? "ok"
          : "review";
        const emissions = (() => {
          const v = mapped.tco2e;
          if (typeof v === "number" && Number.isFinite(v)) return v * 1000;
          const n = parseFloat(String(v || "").replace(",", "."));
          return Number.isFinite(n) ? n * 1000 : 0;
        })();
        return {
          description: String(mapped.description || mapped.route_category || mapped.route_main || "").trim(),
          flow: flow.displayFlow,
          amount: mapped.quantity,
          unit: String(mapped.unit || "").trim(),
          emissions,
          status,
        };
      });

      setParsed({
        filename: file.name,
        companyName: meta.companyName,
        cvr: meta.cvr,
        yearStart: meta.yearStart,
        yearEnd: meta.yearEnd,
        yearLabel: meta.yearLabel,
        mapping,
        rawRows: sheetParsed.rows || [],
        uiRows: rows,
      });
      setTimeout(() => setStep(2), 500);
    } catch (e) {
      console.error(e);
      setParseError(String(e?.message || "Kunne ikke læse filen. Prøv en .xlsx eksport.")); // user-facing
    }
  };

  const onPickClick = () => fileRef.current?.click();
  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = "";
  };
  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const startImport = async () => {
    if (!businessId || !parsed) return;
    setLoading(true);
    setProgress(0);
    setImportResult(null);

    let raf = 0;
    const start = Date.now();
    const tick = () => {
      const t = Math.min(0.92, (Date.now() - start) / 1200);
      setProgress(t * 100);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    try {
      const outcome = await applyYfsImport({
        businessId,
        rawRows: parsed.rawRows,
        columnMapping: parsed.mapping,
      });
      setImportResult(outcome);
      setProgress(100);
      setStep(3);
    } catch (e) {
      console.error(e);
      setParseError(String(e?.message || e));
    } finally {
      cancelAnimationFrame(raf);
      setLoading(false);
    }
  };

  const cvrMismatch =
    parsed && facilityVat && parsed.cvr && parsed.cvr.replace(/\D/g, "") !== String(facilityVat).replace(/\D/g, "");

  const uiRows = parsed?.uiRows || [];
  const rowCount = uiRows.length;
  const totalEmissions = uiRows.reduce((s, r) => s + (r.emissions || 0), 0);
  const missingPrice = uiRows.filter((r) => r.status === "missing-price").length;
  const reviewCount = uiRows.filter((r) => r.status === "review").length;

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center p-4"
      style={{ background: "rgba(4,44,83,0.35)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white overflow-hidden"
        style={{
          width: 720,
          maxWidth: "calc(100vw - 32px)",
          borderRadius: 18,
          boxShadow: "0 24px 80px rgba(4,44,83,0.22)",
          fontFamily: "'DM Sans', system-ui, sans-serif",
          color: NAVY,
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-full hover:bg-black/5 z-10"
          style={{ color: "white" }}
          type="button"
        >
          <X className="h-4 w-4" />
        </button>

        <StepIndicator step={step} />

        <div className="px-6 pt-5 pb-5">
          {step === 1 && !loading && (
            <Step1
              onPickClick={onPickClick}
              onDrop={onDrop}
              parsedName={parsed?.filename}
            />
          )}
          {parseError ? (
            <div
              className="mt-3 px-3 py-2 text-xs rounded-md"
              style={{
                background: "rgba(220,38,38,0.12)",
                color: "#b91c1c",
                border: "1px solid rgba(220,38,38,0.22)",
              }}
            >
              {parseError}
            </div>
          ) : null}
          {step === 2 && !loading && parsed && (
            <Step2
              parsed={parsed}
              showData={showData}
              setShowData={setShowData}
              cvrMismatch={!!cvrMismatch}
              periodOptions={accountingPeriodOptions}
              selectedPeriod={selectedPeriod}
              setSelectedPeriod={setSelectedPeriod}
            />
          )}
          {loading && <LoadingState rowCount={rowCount} progress={progress} />}
          {step === 3 && !loading && (
            <Step3
              rowCount={rowCount}
              totalEmissions={totalEmissions}
              missingPrice={missingPrice}
              reviewCount={reviewCount}
              yearLabel={parsed?.yearLabel ?? "—"}
              companyName={parsed?.companyName ?? ""}
              importedCount={importResult?.imported}
            />
          )}
        </div>

        {!loading && (
          <div
            className="px-6 py-4 flex items-center justify-between gap-3"
            style={{ borderTop: `1px solid ${BORDER}`, background: SURFACE }}
          >
            {step === 1 && (
              <>
                <GhostBtn onClick={onClose}>Annuller</GhostBtn>
                <PrimaryBtn disabled={!parsed} onClick={() => setStep(2)}>
                  Næste →
                </PrimaryBtn>
              </>
            )}
            {step === 2 && (
              <>
                <GhostBtn onClick={() => setStep(1)}>← Tilbage</GhostBtn>
                <AmberBtn onClick={startImport}>
                  <Download className="h-4 w-4" /> Importer til flow
                </AmberBtn>
              </>
            )}
            {step === 3 && (
              <>
                <GhostBtn onClick={onClose}>Luk</GhostBtn>
                <PrimaryBtn onClick={onClose}>Gå til regnskabet →</PrimaryBtn>
              </>
            )}
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={onFileChange}
        />
      </div>
    </div>
  );
}

const StepIndicator = ({ step }) => {
  const items = [
    { n: 1, label: "Indhold" },
    { n: 2, label: "Bekræft" },
    { n: 3, label: "Importeret" },
  ];
  return (
    <div className="flex items-stretch" style={{ background: NAVY }}>
      {items.map((it, i) => {
        const done = step > it.n;
        const active = step === it.n;
        const idle = !done && !active;
        const badgeBg = done ? GREEN : active ? AMBER : "rgba(255,255,255,0.15)";
        const badgeColor = done ? "white" : active ? NAVY : "rgba(255,255,255,0.7)";
        const labelColor = done ? "white" : active ? AMBER : "rgba(255,255,255,0.55)";
        return (
          <div key={it.n} className="flex-1 flex items-center justify-center gap-2 py-3 relative">
            <div
              className="h-6 w-6 rounded-full grid place-items-center text-xs font-semibold"
              style={{ background: badgeBg, color: badgeColor }}
            >
              {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : it.n}
            </div>
            <span className="text-xs font-medium" style={{ color: labelColor, opacity: idle ? 0.7 : 1 }}>
              {it.label}
            </span>
            {i < items.length - 1 && (
              <div className="absolute right-0 top-2 bottom-2 w-px" style={{ background: "rgba(255,255,255,0.15)" }} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const Step1 = ({ onPickClick, onDrop, parsedName }) => (
  <div>
    <h2 className="text-lg font-semibold" style={{ color: NAVY }}>
      Indhold / Content
    </h2>
    <p className="text-sm mt-1" style={{ color: "rgba(4,44,83,0.65)" }}>
      Upload din fil — vi finder virksomhed og regnskabsår automatisk.
    </p>

    <button
      type="button"
      onClick={onPickClick}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className="mt-4 w-full flex flex-col items-center justify-center text-center transition hover:opacity-90"
      style={{
        border: "2px dashed rgba(4,44,83,0.13)",
        background: SURFACE,
        borderRadius: 12,
        padding: 24,
      }}
    >
      <div
        className="h-10 w-10 rounded-full grid place-items-center mb-2"
        style={{ background: "white", border: `1px solid ${BORDER}` }}
      >
        <ArrowDownToLine className="h-5 w-5" style={{ color: NAVY }} />
      </div>
      <div className="text-sm font-semibold" style={{ color: NAVY }}>
        {parsedName ? parsedName : "Træk og slip — eller klik for at vælge"}
      </div>
      <div className="text-xs mt-1" style={{ color: "rgba(4,44,83,0.55)" }}>
        .xlsx · .xls · .csv · Klimakompasset
      </div>
    </button>
  </div>
);

const Step2 = ({
  parsed,
  showData,
  setShowData,
  cvrMismatch,
  periodOptions,
  selectedPeriod,
  setSelectedPeriod,
}) => {
  const rowCount = parsed.uiRows.length || 7;
  const options = (() => {
    const set = new Set(periodOptions);
    if (parsed.yearStart && parsed.yearEnd) {
      set.add(prettyPeriod(parsed.yearStart, parsed.yearEnd));
    }
    return Array.from(set);
  })();
  return (
    <div>
      <h2 className="text-lg font-semibold">Klar til at importere</h2>
      <p className="text-sm mt-1" style={{ color: "rgba(4,44,83,0.65)" }}>
        Vi fandt {rowCount} rækker i din fil. Tryk Importer for at indsætte dem i flowet.
      </p>

      <div
        className="mt-4 p-4"
        style={{
          background: GREEN_BG,
          border: `1px solid rgba(15,110,86,0.25)`,
          borderRadius: 12,
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="h-9 w-9 rounded-lg grid place-items-center shrink-0"
            style={{ background: "white", border: `1px solid rgba(15,110,86,0.2)` }}
          >
            <svg width="16" height="18" viewBox="0 0 16 18" fill="none">
              <path d="M2 1.5h7l5 5V16a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5V2a.5.5 0 0 1 .5-.5z" stroke={GREEN} strokeWidth="1.3"/>
              <path d="M9 1.5V6.5h5" stroke={GREEN} strokeWidth="1.3"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" style={{ color: NAVY }}>
              {parsed.filename}
            </div>
            <div className="text-xs mt-0.5" style={{ color: "rgba(4,44,83,0.65)" }}>
              {parsed.companyName || "—"} · CVR {parsed.cvr || "—"} · Klimakompasset
            </div>
          </div>
          <div className="text-2xl font-bold leading-none" style={{ color: GREEN }}>
            {rowCount}
          </div>
        </div>

        {cvrMismatch && (
          <div
            className="mt-3 px-3 py-2 text-xs rounded-md"
            style={{ background: "rgba(255,204,0,0.18)", color: NAVY, border: "1px solid rgba(255,204,0,0.5)" }}
          >
            CVR i filen matcher ikke facilitetens VAT NO. Du kan stadig importere.
          </div>
        )}

        <div
          className="mt-3 flex items-center justify-between gap-2 px-3 py-2 rounded-lg"
          style={{ background: "white", border: `1px solid ${BORDER}` }}
        >
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider" style={{ color: "rgba(4,44,83,0.55)" }}>
              Importeres til regnskabsår
            </div>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="mt-0.5 w-full bg-transparent text-sm font-semibold outline-none cursor-pointer"
              style={{ color: NAVY }}
            >
              {!selectedPeriod && <option value="">Vælg regnskabsperiode…</option>}
              {options.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div
            className="h-7 w-7 rounded-full grid place-items-center shrink-0"
            style={{ background: selectedPeriod ? NAVY : "rgba(4,44,83,0.2)" }}
          >
            <Check className="h-4 w-4" strokeWidth={3} style={{ color: AMBER }} />
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowData(!showData)}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium hover:opacity-80"
          style={{ color: "rgba(4,44,83,0.7)" }}
        >
          <ChevronRight
            className="h-3.5 w-3.5 transition-transform"
            style={{ transform: showData ? "rotate(90deg)" : "none" }}
          />
          {showData ? "Skjul data" : "Se data der overføres"}
        </button>

        {showData && (
          <div
            className="mt-2 overflow-auto rounded-md"
            style={{ maxHeight: 320, background: "white", border: `1px solid ${BORDER}` }}
          >
            <table className="w-full text-xs">
              <thead style={{ background: SURFACE, position: "sticky", top: 0 }}>
                <tr>
                  {["Beskrivelse", "Flow", "Mængde", "tCO₂e", "Status"].map((h) => (
                    <th key={h} className="text-left px-2 py-1.5 font-semibold" style={{ color: NAVY }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(parsed.uiRows.length > 0 ? parsed.uiRows : []).map((r, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${BORDER}` }}>
                    <td className="px-2 py-1.5 truncate max-w-[140px]">{r.description || "—"}</td>
                    <td className="px-2 py-1.5">{r.flow}</td>
                    <td className="px-2 py-1.5">
                      {typeof r.amount === "number" ? r.amount.toLocaleString("da-DK") : String(r.amount || "")} {r.unit}
                    </td>
                    <td className="px-2 py-1.5">{(r.emissions / 1000).toFixed(2)}</td>
                    <td className="px-2 py-1.5">
                      <StatusBadge status={r.status} />
                    </td>
                  </tr>
                ))}
                {parsed.uiRows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-2 py-3 text-center" style={{ color: "rgba(4,44,83,0.5)" }}>
                      Ingen rækker fundet i datablad.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  if (status === "ok")
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold"
        style={{ background: GREEN_BG, color: GREEN }}>
        ✓
      </span>
    );
  if (status === "missing-price")
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold"
        style={{ background: "rgba(255,204,0,0.2)", color: "#7a5d00" }}>
        Mangler pris
      </span>
    );
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold"
      style={{ background: "rgba(220,38,38,0.12)", color: "#b91c1c" }}>
      Review
    </span>
  );
};

const LoadingState = ({ rowCount, progress }) => (
  <div className="py-8 flex flex-col items-center text-center">
    <div
      className="h-12 w-12 rounded-full animate-spin"
      style={{
        border: "3px solid rgba(4,44,83,0.12)",
        borderTopColor: NAVY,
      }}
    />
    <h3 className="mt-4 text-base font-semibold" style={{ color: NAVY }}>
      Importerer {rowCount || 7} rækker...
    </h3>
    <p className="text-xs mt-1" style={{ color: "rgba(4,44,83,0.6)" }}>
      Placerer i flows — et øjeblik
    </p>
    <div className="mt-4 w-full h-1 rounded-full overflow-hidden" style={{ background: "rgba(4,44,83,0.08)" }}>
      <div className="h-full transition-all" style={{ width: `${progress}%`, background: NAVY }} />
    </div>
  </div>
);

const Step3 = ({
  rowCount,
  totalEmissions,
  missingPrice,
  reviewCount,
  yearLabel,
  companyName,
  importedCount,
}) => {
  const tco2e = (totalEmissions / 1000).toFixed(0);
  const displayCount = importedCount != null ? Number(importedCount) : (rowCount || 7);
  return (
    <div className="flex flex-col items-center text-center pt-2 pb-1">
      <div
        className="h-16 w-16 rounded-full grid place-items-center"
        style={{
          background: GREEN,
          animation: "yfs-pop 420ms cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        }}
      >
        <Check className="h-8 w-8" strokeWidth={3} style={{ color: "white" }} />
      </div>
      <style>{`@keyframes yfs-pop { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
      <h3 className="mt-4 text-lg font-semibold" style={{ color: NAVY }}>
        {displayCount} rækker importeret
      </h3>
      <p className="text-sm mt-1" style={{ color: "rgba(4,44,83,0.65)" }}>
        Data er nu i {companyName || "facilitetens"}{companyName ? "'" : ""} regnskab for {yearLabel}.
        {reviewCount > 0 && ` ${reviewCount} linje${reviewCount === 1 ? "" : "r"} kræver review`}
        {reviewCount > 0 && missingPrice > 0 && " · "}
        {missingPrice > 0 && `${missingPrice} linje${missingPrice === 1 ? "" : "r"} mangler pris`}
        .
      </p>
      <div className="mt-4 grid grid-cols-3 gap-2 w-full">
        <StatBox value={String(displayCount)} label="Importeret" bg={GREEN_BG} color={GREEN} />
        <StatBox value={tco2e} label="tCO₂e" bg="rgba(4,44,83,0.06)" color={NAVY} />
        <StatBox value={String(missingPrice)} label="Mangler pris" bg="rgba(255,204,0,0.18)" color="#7a5d00" />
      </div>
    </div>
  );
};

const StatBox = ({ value, label, bg, color }) => (
  <div className="px-2 py-2.5 rounded-lg text-center" style={{ background: bg }}>
    <div className="text-base font-bold leading-none" style={{ color }}>{value}</div>
    <div className="text-[10px] mt-1 font-medium" style={{ color }}>{label}</div>
  </div>
);

const GhostBtn = ({ children, onClick }) => (
  <button
    onClick={onClick}
    type="button"
    className="px-4 py-2 rounded-full text-sm font-medium transition hover:bg-black/5"
    style={{ color: NAVY }}
  >
    {children}
  </button>
);

const PrimaryBtn = ({ children, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    type="button"
    className="px-4 py-2 rounded-full text-sm font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
    style={{ background: NAVY, color: AMBER }}
  >
    {children}
  </button>
);

const AmberBtn = ({ children, onClick }) => (
  <button
    onClick={onClick}
    type="button"
    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition hover:opacity-90"
    style={{ background: AMBER, color: NAVY }}
  >
    {children}
  </button>
);

